import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Guest, HolidayVideos } from '../../assests/ExportImageIcon'
import MainTab from '../../componants/mainTab/MainTab'
import DatePicker from '../../componants/datePicker/DatePicker'
import Modal from '../../componants/modal/Modal'
import service from '../../services/service'
import endpoints from '../../services/endpoints'
import { DESTINATION_TYPES } from './holidaysPackagesData'
import HolidayPackageCarousel from '../../componants/holidayPackageCarousel/HolidayPackageCarousel'
import PopularDestination from '../popularDestination/PopularDestination'
import SpecialOffer from '../specialOffer/SpecialOffer'
import InfluencerReels from '../influencerReels/InfluencerReels'
import GetApp from '../getApp/GetApp'
import OurLatestBlog from '../ourLatestblog/OurLatestBlog'
import './holidays.css'

const todayIso = () => new Date().toISOString().slice(0, 10)

// Matches the "Total 4 guests (Max. 3 adults) allowed in a room" rule from
// the reference screenshot — looser than hotel.jsx's room limits, so this
// stays a local holidays-only copy instead of reusing utils/hotelOccupancy.
const HOLIDAY_MAX_ADULTS_PER_ROOM = 3
const HOLIDAY_MAX_GUESTS_PER_ROOM = 4
const HOLIDAY_MAX_ROOMS = 6
const HOLIDAY_ROOM_LIMIT_MESSAGE = `Total ${HOLIDAY_MAX_GUESTS_PER_ROOM} guests (Max. ${HOLIDAY_MAX_ADULTS_PER_ROOM} adults) allowed in a room`

const Stepper = ({ value, onMinus, onPlus, minusDisabled = false, plusDisabled = false }) => (
  <div className="hotelGuestsModal__stepper">
    <button type="button" className="hotelGuestsModal__stepBtn" onClick={onMinus} disabled={minusDisabled} aria-label="Decrease">−</button>
    <span className="hotelGuestsModal__stepValue">{value}</span>
    <button type="button" className="hotelGuestsModal__stepBtn" onClick={onPlus} disabled={plusDisabled} aria-label="Increase">+</button>
  </div>
)

const LocationPinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22s7-7.05 7-12a7 7 0 1 0-14 0c0 4.95 7 12 7 12Z" stroke="#6B7280" strokeWidth="1.5" />
    <circle cx="12" cy="10" r="2.5" stroke="#6B7280" strokeWidth="1.5" />
  </svg>
)

const INDIA_COUNTRY_NAME = 'India'

// /hotels/hotel/city/suggestion/ (used below for the visible dropdown, to
// match hotel search exactly) returns no `id` field at all — so a selection
// from it alone can't feed /packages/search/'s numeric source/destination,
// which silently breaks the real search/list chain. /users/cities/{country_id}
// DOES have real {id, name} cities (confirmed: Noida=16283, South Goa=144351 —
// exact matches for the ids in every real curl shared) — so it's used here
// purely as a background id lookup by name, after a domestic city is picked,
// without changing what the dropdown itself shows.
// One cached promise per country name — the countries list is fetched once,
// each country's city list once, so repeat picks resolve instantly.
let countriesPromise = null
const cityListPromises = {}

const fetchCountries = () => {
  if (countriesPromise) return countriesPromise
  countriesPromise = service
    .get(endpoints.users.countries, {}, { silent: true })
    .then(({ data }) => data?.data || [])
    .catch(() => {
      countriesPromise = null
      return []
    })
  return countriesPromise
}

const fetchCitiesOfCountry = (countryName) => {
  const key = (countryName || INDIA_COUNTRY_NAME).trim().toLowerCase()
  if (cityListPromises[key]) return cityListPromises[key]
  cityListPromises[key] = fetchCountries()
    .then((countries) => {
      const country = countries.find((c) => (c.name || '').trim().toLowerCase() === key)
      if (!country) return []
      return service
        .get(endpoints.users.cities.replace('{country_id}', country.id), {}, { silent: true })
        .then(({ data: cityData }) => cityData?.data || [])
    })
    .catch(() => {
      delete cityListPromises[key]
      return []
    })
  return cityListPromises[key]
}

// Resolves a suggestion's city name to the numeric id /packages/search/ needs,
// looking in that suggestion's own country (India if the suggestion has none).
const resolveCityId = async (cityName, countryName) => {
  const cities = await fetchCitiesOfCountry(countryName)
  const needle = (cityName || '').trim().toLowerCase()
  if (!needle) return null
  const match = cities.find((city) => (city.name || '').trim().toLowerCase() === needle)
  return match?.id || null
}

// Same city search hotel.jsx already uses (GET /hotels/hotel/city/suggestion/)
// — holidays needs two of these (From/To), so it's a small local component
// instead of duplicating the fetch/debounce/outside-click wiring twice.
const CityField = ({ placeholder, onSelect }) => {
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [open, setOpen] = useState(false)
  const [openUp, setOpenUp] = useState(false)
  const wrapRef = useRef(null)
  const debounceRef = useRef(null)
  const latestSelectionRef = useRef(null)

  const fetchSuggestions = useCallback((q) => {
    if (!q || q.length < 2) {
      setSuggestions([])
      setOpen(false)
      return
    }

    // Same city list hotel search uses (GET /hotels/hotel/city/suggestion/)
    // for both tabs, filtered to domestic (India) vs international (not
    // India) — matches what a user already sees searching hotels, instead of
    // a separate city source. Note: this endpoint returns no `id` field at
    // all, so a selection here can't feed /packages/search/'s numeric
    // source/destination — the real package search only fires when both
    // picks happen to come with an id another way; otherwise the page falls
    // back to browsing every mock package, same as before this was wired up.
    service
      .get(endpoints.hotel.citySuggestion, { location: q, page: 1 }, { silent: true })
      .then(({ data }) => {
        const list = data?.data || data?.results || data || []
        const items = Array.isArray(list) ? list : []
        // Domestic (India) / International (non-India) filtering of this list
        // is switched off — the dropdown now behaves like the normal hotel
        // city search for both tabs.
        // const filtered = items.filter((item) => {
        //   const country = (item.countryName || item.country_name || item.country || '').trim().toLowerCase()
        //   const isIndia = country === INDIA_COUNTRY_NAME.toLowerCase()
        //   return domesticOnly ? isIndia : !isIndia
        // })
        setSuggestions(items)
        setOpen(true)
      })
      .catch(() => setSuggestions([]))
  }, [])

  // Switching Domestic/International used to clear whatever was typed or
  // selected (it was filtered per tab). Not needed now that the list is the same.
  // useEffect(() => {
  //   setQuery('')
  //   setSuggestions([])
  //   setOpen(false)
  //   onSelect(null)
  //   latestSelectionRef.current = null
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [domesticOnly])

  const handleChange = (e) => {
    const value = e.target.value
    setQuery(value)
    onSelect(null)
    latestSelectionRef.current = null
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 400)
  }

  const handleSelect = (item) => {
    const cityName = item.cityName || item.city_name || item.name || item.city || ''
    setQuery(cityName)
    setSuggestions([])
    setOpen(false)
    latestSelectionRef.current = cityName

    // The visible suggestion has no id — resolve the real one by name in the
    // background (near-instant after the first lookup, since the city list
    // is cached) so the Search button can actually call /packages/search/.
    // Looked up in the suggestion's own country (e.g. Pattaya -> Thailand's
    // city list); if no match, the city keeps no id and falls back to
    // browsing. Guarded against a race where the user picks a
    // different city before this resolves — only applies if this is still
    // the latest selection.
    onSelect({ ...item, cityName, id: item.id ?? null })
    const countryName = item.countryName || item.country_name || item.country || ''
    resolveCityId(cityName, countryName).then((id) => {
      if (id && latestSelectionRef.current === cityName) onSelect({ ...item, cityName, id })
    })
  }

  useEffect(() => {
    if (!open) return undefined
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open])

  useEffect(() => {
    if (!open || !wrapRef.current) return
    const PANEL_HEIGHT = 260
    const rect = wrapRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    setOpenUp(spaceBelow < PANEL_HEIGHT && rect.top > spaceBelow)
  }, [open])

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current) }, [])

  return (
    <div className='hotelSearchField cmnBdr hotelLocationField' ref={wrapRef}>
      <div className='hotelSearchInput'>
        <LocationPinIcon />
        <div>
          <input
            type="text"
            placeholder={placeholder}
            value={query}
            onChange={handleChange}
            onFocus={() => suggestions.length > 0 && setOpen(true)}
            autoComplete="off"
          />
        </div>
      </div>
      {open && suggestions.length > 0 && (
        <ul className={`hotelLocationSuggestions ${openUp ? 'hotelLocationSuggestions--up' : ''}`}>
          {suggestions.map((item, index) => (
            <li
              key={item.id || index}
              className="hotelLocationSuggestions__item"
              onClick={() => handleSelect(item)}
            >
              <LocationPinIcon />
              <div>
                <span className="hotelLocationSuggestions__city">
                  {item.cityName || item.city_name || item.name || item.city || ''}
                </span>
                {(item.countryName || item.country_name || item.country) && (
                  <span className="hotelLocationSuggestions__country">
                    , {item.countryName || item.country_name || item.country}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// "Featured" packages for the home page's Module-1 "Package Cards" section —
// same mock list the listing page filters, just the ones tagged Featured/Popular.
// Featured / Domestic / International rows were built from the static mock
// list (HOLIDAY_PACKAGES) — hidden until there's a real API to feed them.
// const FEATURED_PACKAGES = HOLIDAY_PACKAGES.filter((pkg) => pkg.badge)
// const DOMESTIC_PACKAGES = HOLIDAY_PACKAGES.filter((pkg) => pkg.destinationType === 'Domestic')
// const INTERNATIONAL_PACKAGES = HOLIDAY_PACKAGES.filter((pkg) => pkg.destinationType === 'International')

// No backend yet for Holidays — the results page (/holidays-list) is static
// mock data too, but the search still takes the user there (same UX as every
// other vertical), it just isn't filtering anything server-side yet.
const HolidaysMain = () => {
  const navigate = useNavigate()
  const [selectedFrom, setSelectedFrom] = useState(null)
  const [selectedTo, setSelectedTo] = useState(null)
  const [travelDate, setTravelDate] = useState('')
  const [destinationType, setDestinationType] = useState('Domestic')

  const [guestsOpen, setGuestsOpen] = useState(false)
  const [rooms, setRooms] = useState([{ adults: 2, children: 0 }])
  const [draftRooms, setDraftRooms] = useState([{ adults: 2, children: 0 }])
  const [roomLimitNotes, setRoomLimitNotes] = useState({})
  const [searching, setSearching] = useState(false)

  const openGuestsModal = () => {
    setDraftRooms(rooms.map((room) => ({ ...room })))
    setRoomLimitNotes({})
    setGuestsOpen(true)
  }

  const setRoomNote = (roomIndex, message) => {
    setRoomLimitNotes((prev) => ({ ...prev, [roomIndex]: message }))
  }

  const addRoom = () => {
    setDraftRooms((prev) => (prev.length >= HOLIDAY_MAX_ROOMS ? prev : [...prev, { adults: 1, children: 0 }]))
  }

  const removeRoom = (roomIndex) => {
    setDraftRooms((prev) => prev.filter((_, index) => index !== roomIndex))
    setRoomLimitNotes((prev) => {
      const next = { ...prev }
      delete next[roomIndex]
      return next
    })
  }

  const updateRoomAdults = (roomIndex, delta) => {
    setDraftRooms((prev) => prev.map((room, index) => {
      if (index !== roomIndex) return room
      const nextAdults = room.adults + delta
      if (delta > 0 && (nextAdults > HOLIDAY_MAX_ADULTS_PER_ROOM || nextAdults + room.children > HOLIDAY_MAX_GUESTS_PER_ROOM)) {
        setRoomNote(roomIndex, HOLIDAY_ROOM_LIMIT_MESSAGE)
        return room
      }
      setRoomNote(roomIndex, '')
      return { ...room, adults: Math.max(1, Math.min(HOLIDAY_MAX_ADULTS_PER_ROOM, nextAdults)) }
    }))
  }

  const updateRoomChildren = (roomIndex, delta) => {
    setDraftRooms((prev) => prev.map((room, index) => {
      if (index !== roomIndex) return room
      const nextChildren = room.children + delta
      if (delta > 0 && room.adults + nextChildren > HOLIDAY_MAX_GUESTS_PER_ROOM) {
        setRoomNote(roomIndex, HOLIDAY_ROOM_LIMIT_MESSAGE)
        return room
      }
      setRoomNote(roomIndex, '')
      return { ...room, children: Math.max(0, nextChildren) }
    }))
  }

  const applyGuests = () => {
    setRooms(draftRooms.map((room) => ({ ...room })))
    setGuestsOpen(false)
  }

  const totalAdults = rooms.reduce((sum, room) => sum + room.adults, 0)
  const totalChildren = rooms.reduce((sum, room) => sum + room.children, 0)
  const guestsLabel = [
    `${totalAdults} Adult${totalAdults > 1 ? 's' : ''}`,
    totalChildren > 0 ? `${totalChildren} Child${totalChildren > 1 ? 'ren' : ''}` : null,
    `${rooms.length} Room${rooms.length > 1 ? 's' : ''}`,
  ].filter(Boolean).join(', ')

  // Destination left optional for now — the mock package list only covers a
  // handful of places (Thailand/Goa/Kerala), so requiring it just blocked
  // search for any other destination someone typed. Empty search still takes
  // you to the listing showing every package.
  const handleSearchHolidays = () => {
    const destinationName = selectedTo?.cityName || selectedTo?.city_name || selectedTo?.name || selectedTo?.city || ''

    // POST /packages/search/ needs real numeric city ids (source/destination),
    // not free text — without both selected from the autocomplete, fall back
    // to the old "browse everything" navigate instead of sending a bad payload.
    if (!selectedFrom?.id || !selectedTo?.id) {
      navigate('/holidays-list', {
        state: { from: selectedFrom, destination: destinationName, travelDate, destinationType, rooms },
      })
      return
    }

    const payload = {
      payload: {
        source: selectedFrom.id,
        destination: selectedTo.id,
        rooms: rooms.map((room, index) => ({
          room_no: index + 1,
          adult: room.adults,
          child: room.children,
          traveler: room.adults + room.children,
        })),
        travel_date: travelDate || todayIso(),
      },
      category: [],
      destination_type: destinationType ? destinationType.toLowerCase() : undefined,
      inclusion: [],
      sort_by: 'newest',
      page: 1,
      page_size: 10,
    }

    setSearching(true)
    service
      .post(endpoints.packages.search, payload, { timeout: 45000 })
      .then(({ data }) => {
        navigate('/holidays-list', {
          state: {
            from: selectedFrom,
            destination: destinationName,
            travelDate,
            destinationType,
            rooms,
            // Confirmed real response: { status, count, data: { payload, packages: [...] } }
            packagesApiResult: data?.data ?? data,
            packagesCount: data?.count,
            // Carried forward to holidayDetail.jsx — POST /packages/content/
            // needs this exact same source/destination/rooms/travel_date
            // shape (plus package_id) to fetch a package's real content.
            searchContext: {
              source: payload.payload.source,
              destination: payload.payload.destination,
              rooms: payload.payload.rooms,
              travelDate: payload.payload.travel_date,
              destinationType: payload.destination_type,
            },
          },
        })
      })
      .catch(() => {
        // error toast already shown by the service interceptor
      })
      .finally(() => setSearching(false))
  }

  // "Package Detail Access" (Module 1)
  const handlePackageClick = (pkg) => {
    navigate(`/holidays-detail/${pkg.id}`)
  }

  return (
    <>
      <section className='banner'>
        <div className='videoPart'>
          <video className='promoBanner' autoPlay loop muted playsinline="" data-object-fit="cover" preload="metadata">
            <source src={HolidayVideos} type="video/mp4" />
          </video>
        </div>
        <div className='bannerContent'>
          <div className='container'>
            <div className='row g-4'>
              <div className='col-md-12'>
                <h1>Discover Your Next Holiday</h1>
                <MainTab />
              </div>

              <div className='col-md-12'>
                <div className='bookingAreaanner'>
                  {/* Module 1: "Quick Tabs / Shortcuts — quick access to
                      Domestic and International travel packages." */}
                  <div className='commonBookingTab commonBookingTab--split holidayDestinationTypeTabs'>
                    {DESTINATION_TYPES.map((type) => (
                      <div
                        key={type}
                        className={`bookTab ${destinationType === type ? 'active' : ''}`}
                        onClick={() => setDestinationType(type)}
                      >
                        {type}
                      </div>
                    ))}
                  </div>
                  <div className='hotelSearchArea mt-0'>
                    <div className='hotelSearchUp'>
                      {/* Previous plain text From/To stubs — replaced below with
                          real city search (CityField, GET /hotels/hotel/city/suggestion/).
                          <div className='hotelSearchField cmnBdr'>
                            <div className='hotelSearchInput'>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 22s7-7.05 7-12a7 7 0 1 0-14 0c0 4.95 7 12 7 12Z" stroke="#6B7280" strokeWidth="1.5" />
                                <circle cx="12" cy="10" r="2.5" stroke="#6B7280" strokeWidth="1.5" />
                              </svg>
                              <div>
                                <input type="text" placeholder="From" />
                              </div>
                            </div>
                          </div>
                          <div className='hotelSearchField cmnBdr'>
                            <div className='hotelSearchInput'>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 22s7-7.05 7-12a7 7 0 1 0-14 0c0 4.95 7 12 7 12Z" stroke="#6B7280" strokeWidth="1.5" />
                                <circle cx="12" cy="10" r="2.5" stroke="#6B7280" strokeWidth="1.5" />
                              </svg>
                              <div>
                                <input
                                  type="text"
                                  placeholder="To"
                                  value={destination}
                                  onChange={(e) => setDestination(e.target.value)}
                                  autoComplete="off"
                                />
                              </div>
                            </div>
                          </div>
                      */}
                      <CityField placeholder="From" onSelect={setSelectedFrom} />
                      <CityField placeholder="To" onSelect={setSelectedTo} />
                      <div className='hotelSearchField cmnBdr'>
                        <div className='hotelSearchInput'>
                          <div>
                            <DatePicker
                              className='searchBarDateField'
                              placeholder='Travel Date'
                              min={todayIso()}
                              value={travelDate}
                              onChange={setTravelDate}
                            />
                          </div>
                        </div>
                      </div>
                      {/* Previous "Room & Guests" stub was a second DatePicker
                          (copy/paste leftover — a date field can't collect
                          rooms/guests). Replaced below with the real
                          rooms-and-guests popup (Modal + Stepper, matching
                          hotel.jsx's pattern).
                          <div className='hotelSearchField'>
                            <div className='hotelSearchInput'>
                              <div>
                                <DatePicker
                                  className='searchBarDateField'
                                  placeholder='Room & Guests'
                                  min={todayIso()}
                                  value={travelDate}
                                  onChange={setTravelDate}
                                />
                              </div>
                            </div>
                          </div>
                      */}
                      <div className='hotelSearchField hotelGuestsField'>
                        <div className='hotelSearchInput' onClick={openGuestsModal}>
                          <img src={Guest} alt="Guests" />
                          <div>
                            <button type="button" className="hotelGuestsTrigger">
                              {guestsLabel}
                            </button>
                          </div>
                        </div>
                      </div>
                      <button
                        type='button'
                        className='commonBtn'
                        onClick={handleSearchHolidays}
                        disabled={searching}
                      >
                        {searching ? 'Searching...' : 'Search'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Modal open={guestsOpen} onClose={() => setGuestsOpen(false)} maxWidth={430} className="hotelGuestsModalBox">
        <div className="hotelGuestsModal">
          <p className="holidayGuestsModal__banner">{HOLIDAY_ROOM_LIMIT_MESSAGE}</p>

          <div className="hotelGuestsModal__rooms">
            {draftRooms.map((room, roomIndex) => (
              <div className="hotelGuestsModal__room" key={`room-${roomIndex}`}>
                <div className="holidayGuestsModal__roomTitleRow">
                  <div className="hotelGuestsModal__roomTitle">Room {roomIndex + 1}</div>
                  {draftRooms.length > 1 && (
                    <button type="button" className="holidayGuestsModal__removeRoom" onClick={() => removeRoom(roomIndex)}>
                      Remove
                    </button>
                  )}
                </div>
                <div className="hotelGuestsModal__row">
                  <div>
                    <span className="hotelGuestsModal__label">Adults</span>
                    <span className="hotelGuestsModal__hint">Above 12 Years</span>
                  </div>
                  <Stepper
                    value={room.adults}
                    onMinus={() => updateRoomAdults(roomIndex, -1)}
                    onPlus={() => updateRoomAdults(roomIndex, 1)}
                    minusDisabled={room.adults <= 1}
                  />
                </div>
                <div className="hotelGuestsModal__row">
                  <div>
                    <span className="hotelGuestsModal__label">Children</span>
                    <span className="hotelGuestsModal__hint">Below 12 Years</span>
                  </div>
                  <Stepper
                    value={room.children}
                    onMinus={() => updateRoomChildren(roomIndex, -1)}
                    onPlus={() => updateRoomChildren(roomIndex, 1)}
                    minusDisabled={room.children <= 0}
                  />
                </div>
                {roomLimitNotes[roomIndex] ? (
                  <p className="hotelGuestsModal__limit">{roomLimitNotes[roomIndex]}</p>
                ) : null}
              </div>
            ))}
          </div>

          <div className="hotelGuestsModal__footer holidayGuestsModal__footer">
            <button
              type="button"
              className="holidayGuestsModal__addRoom"
              onClick={addRoom}
              disabled={draftRooms.length >= HOLIDAY_MAX_ROOMS}
            >
              + Add Another Room
            </button>
            <button type="button" className="hotelGuestsModal__apply" onClick={applyGuests}>
              APPLY
            </button>
          </div>
        </div>
      </Modal>

      {/* Hidden — these rows ran on mock data, no real API yet:
       Module 1: "Package Cards — thumbnail, title, destination, duration,
          starting price, rating, reviews, tags." "Personalized Recommendations"
          (based on the user's past searches/bookings) isn't buildable as static
          mock data — needs real booking history, deferred until that API exists. 
      <HolidayPackageCarousel
        title='Featured Travel Packages'
        subtitle='Handpicked trips our travellers love the most'
        packages={FEATURED_PACKAGES}
        onSelect={handlePackageClick}
      />

      <HolidayPackageCarousel
        title='Domestic Packages'
        subtitle='Explore the best of India with hotels and sightseeing bundled in'
        packages={DOMESTIC_PACKAGES}
        onSelect={handlePackageClick}
        onViewAll={() => navigate('/holidays-list', { state: { destinationType: 'Domestic' } })}
      />

      <HolidayPackageCarousel
        title='International Packages'
        subtitle='Beaches, cities and cultures across the world'
        packages={INTERNATIONAL_PACKAGES}
        onSelect={handlePackageClick}
        onViewAll={() => navigate('/holidays-list', { state: { destinationType: 'International' } })}
      />
      */}

      {/* <PopularDestination /> */}

      <SpecialOffer />

      <InfluencerReels />

      <GetApp />

      <OurLatestBlog />
    </>
  )
}

export default HolidaysMain
